import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

export function FolderForm({
  parentFolderId,
  onClose,
  onCreated,
}: {
  parentFolderId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () => filesApi.createFolder({ name: name.trim(), parentFolderId }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="folder-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="folder-form-title">{t('files.folder.create')}</h2>
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('files.folder.name')}
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!name.trim()}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
